/**
 * InfinitSales - SSE Service
 * Server-Sent Events for real-time communication
 * Simpler and more stable than WebSocket
 */
import { isJwtExpired } from '../../utils/jwt';

// Flag REACT_APP_SSE_EXP_CHECK (build-time, CRA). Default DESLIGADO =
// comportamento antigo. Ligada: antes de abrir/reabrir o EventSource o
// serviço lê o `exp` do JWT e, se vencido, NÃO reconecta — emite AUTH_EXPIRED
// para o AuthContext encerrar a sessão. Medido em prod: o JWT dura 24 h sem
// renovação e uma aba aberta há mais de um dia ficava num laço de reconexão
// com token vencido (401) a 1.200-1.900 req/h, com o tempo real morto e sem
// aviso ao usuário.
export const SSE_EXP_CHECK_ENABLED = process.env.REACT_APP_SSE_EXP_CHECK === 'true';

// Probe de sessão (sem flag, 2026-10-13). O EventSource não expõe o status
// HTTP: um token com assinatura inválida (chave do JWT trocada na migração
// para a VPS) não "vence" pelo `exp` e a aba insistia no /events a ~100
// req/min com 401. Quando o EventSource falha, o serviço consulta
// GET /events/auth (mesma validação do /events) com o token: 401 → encerra a
// sessão e NÃO reconecta; 204/5xx/rede → backoff de sempre. No máximo 1
// probe em voo e AUTH_PROBE_MIN_INTERVAL_MS entre probes; nunca roda com a
// conexão saudável.
export const AUTH_PROBE_MIN_INTERVAL_MS = 2_000;

// Event types (must match backend SSEEventTypes)
export const SSE_EVENTS = {
  // Connection events
  CONNECTED: 'CONNECTED',
  HEARTBEAT: 'HEARTBEAT',
  // Só no cliente (não vem do backend): o JWT guardado venceu e o serviço
  // parou de reconectar. Quem assina encerra a sessão (AuthContext).
  AUTH_EXPIRED: 'AUTH_EXPIRED',
  
  // WhatsApp connection events
  CONNECTION_UPDATE: 'CONNECTION_UPDATE',
  QRCODE_UPDATED: 'QRCODE_UPDATED',
  
  // Message events
  NEW_MESSAGE: 'NEW_MESSAGE',
  MESSAGE_STATUS: 'MESSAGE_STATUS',
  MESSAGE_DELETED: 'MESSAGE_DELETED',
  // Reação (emoji) numa mensagem existente — chip na bolha alvo, não bolha nova.
  MESSAGE_REACTION: 'MESSAGE_REACTION',
  MESSAGE_EDITED: 'MESSAGE_EDITED',
  
  // Chat events
  CHAT_UPDATE: 'CHAT_UPDATE',
  CHATS_SYNC: 'CHATS_SYNC',
  
  // Presence events
  PRESENCE_UPDATE: 'PRESENCE_UPDATE',
  
  // Contact events
  CONTACT_UPDATE: 'CONTACT_UPDATE',
  CONTACT_LABELS_CHANGED: 'CONTACT_LABELS_CHANGED',
  CONTACT_TRANSFERRED: 'CONTACT_TRANSFERRED',
  // Responsável pelo contato mudou (membro ou agente) — tag do chat list e
  // drawer atualizam em tempo real entre membros.
  CONTACT_ASSIGNMENT_CHANGED: 'CONTACT_ASSIGNMENT_CHANGED',
  // Motor de propriedade: contato mudou de dono entre workspaces. Allowlist —
  // evento não registrado aqui é descartado em silêncio pelo EventSource.
  CONTACT_OWNERSHIP_CHANGED: 'CONTACT_OWNERSHIP_CHANGED',

  // Agente ligado/desligado (global ou por contato) — reflete entre membros.
  AUTOPILOT_CHANGED: 'AUTOPILOT_CHANGED',
  // Agente retomado num contato — limpa o handoff (banner/tag) entre membros.
  AGENT_RESUMED: 'AGENT_RESUMED',
  // CONTACT_STAGE_CHANGED apagado em 2026-10-20 (DIV-25): nunca foi emitido; a etapa viaja em CONTACT_LABELS_CHANGED.

  // In-app notifications (feed do sino)
  NOTIFICATION_CREATED: 'NOTIFICATION_CREATED',
  NOTIFICATION_READ: 'NOTIFICATION_READ',

  // Sync events (WA Business)
  SYNC_STARTED: 'SYNC_STARTED',
  SYNC_PROGRESS: 'SYNC_PROGRESS',
  SYNC_CHAT_ADDED: 'SYNC_CHAT_ADDED',
  SYNC_COMPLETED: 'SYNC_COMPLETED',
  SYNC_ERROR: 'SYNC_ERROR',

  // Account lifecycle (WA Business)
  ACCOUNT_DISCONNECTED: 'ACCOUNT_DISCONNECTED',

  // Campaign events (Fase 2)
  CAMPAIGN_STARTED: 'CAMPAIGN_STARTED',
  CAMPAIGN_PROGRESS: 'CAMPAIGN_PROGRESS',
  CAMPAIGN_PAUSED: 'CAMPAIGN_PAUSED',
  CAMPAIGN_COMPLETED: 'CAMPAIGN_COMPLETED',
  CAMPAIGN_FAILED: 'CAMPAIGN_FAILED',

  // Importacao de contatos em job (2026-10-20): progresso por lote e fim.
  CONTACT_IMPORT_PROGRESS: 'CONTACT_IMPORT_PROGRESS',

  // System events
  ERROR: 'ERROR',
};

// Connection states
export const SSE_STATES = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  FAILED: 'failed',
};

class SSEService {
  constructor() {
    this.eventSource = null;
    this.state = SSE_STATES.DISCONNECTED;
    this.listeners = new Map();
    this.token = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectTimeout = null;
    // AUTH_EXPIRED é emitido uma vez por token vencido (zera ao conectar com
    // token válido) — sem isto cada visibilitychange re-emitiria.
    this.authExpiredNotified = false;
    // Probe de sessão: promessa em voo (dedupe) e instante do último probe.
    this.authProbe = null;
    this.lastAuthProbeAt = 0;

    // Bind methods
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
  }

  /**
   * Com a flag ligada: o token (o passado ou o guardado) já venceu?
   * Token sem `exp` nunca conta como vencido.
   */
  tokenExpired(token = this.token) {
    return SSE_EXP_CHECK_ENABLED && !!token && isJwtExpired(token);
  }

  /**
   * Token vencido: fecha tudo, impede qualquer reconexão (token = null +
   * contador no máximo), estado FAILED e avisa AUTH_EXPIRED (uma vez).
   */
  failAuthExpired(reason = 'token_expired') {
    console.warn(`[SSE] Sessão inválida (${reason}) — não vou reconectar; sessão precisa de novo login`);
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.token = null;
    this.reconnectAttempts = this.maxReconnectAttempts;
    this.state = SSE_STATES.FAILED;
    this.emit('stateChange', { state: this.state, reason: 'auth_expired' });
    if (!this.authExpiredNotified) {
      this.authExpiredNotified = true;
      this.emit(SSE_EVENTS.AUTH_EXPIRED, { reason });
    }
  }

  /**
   * Base da API para o SSE e para o probe de sessão ('' = URL relativa,
   * mesma origem da página).
   */
  getApiBase() {
    // Em desenvolvimento o CRA dev server tem proxy para o backend
    // (package.json "proxy"), então usamos URL RELATIVA: o EventSource fica
    // na MESMA origem da página (localhost:3000 ou o domínio do túnel), os
    // cookies fluem e o proxy entrega no backend. Apontar direto para o
    // API_BASE_URL aqui quebrava o realtime quando a página e a API estavam
    // em origens diferentes (ex: página local + API via ngrok).
    if (process.env.NODE_ENV === 'development') {
      // Backend local numa OUTRA porta (fora do proxy do CRA, ex.: :8002 quando
      // o :8000 é de outro projeto): aponta o SSE DIRETO para ela — senão o
      // EventSource relativo cai no proxy do package.json (backend errado) e dá
      // "Conexão perdida". CORS '*' + token na query cobrem o cross-origin.
      // Quando a API NÃO é localhost (ex.: túnel ngrok), mantém a URL relativa
      // same-origin (preserva o fluxo do dev-up).
      const devApi = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      if (/^https?:\/\/(localhost|127\.0\.0\.1):/i.test(devApi)) {
        return devApi;
      }
      return '';
    }

    // Produção: respeita REACT_APP_API_BASE_URL (definido em .env) para
    // permitir apontar para backend em outra origem sem editar codigo.
    const apiUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

    // Remove trailing slash
    return apiUrl.replace(/\/$/, '');
  }

  /**
   * Get SSE URL from API base URL
   */
  getSSEUrl(token) {
    return `${this.getApiBase()}/events?token=${token}`;
  }

  /** URL do probe de sessão — mesma base do /events. */
  getAuthProbeUrl() {
    return `${this.getApiBase()}/events/auth`;
  }

  /**
   * Pergunta ao backend se o token guardado ainda vale (GET /events/auth).
   *
   * Resolve com { status: 'invalid' | 'ok' | 'unknown' | 'skipped', reason }:
   * - 'invalid': 401 — o backend recusou o token (reason = 'expired' |
   *   'invalid' | 'revoked', do cabeçalho X-Session-Invalid ou do corpo
   *   {"detail": {"code": "session_invalid", "reason"}}; 'invalid' se não vier);
   * - 'ok': 204 (token válido; a queda do SSE foi outra coisa);
   * - 'unknown': rede, 5xx, qualquer outro status — não dá para afirmar nada;
   * - 'skipped': sem token, ou probe há menos de AUTH_PROBE_MIN_INTERVAL_MS.
   * Nunca rejeita. Um probe em voo é compartilhado por quem chamar no meio.
   * `fetch` direto (não o axios `api`) para não cair no interceptor de 401.
   */
  probeAuth() {
    if (!this.token) return Promise.resolve({ status: 'skipped' });
    if (this.authProbe) return this.authProbe;
    const now = Date.now();
    if (now - this.lastAuthProbeAt < AUTH_PROBE_MIN_INTERVAL_MS) {
      return Promise.resolve({ status: 'skipped' });
    }
    this.lastAuthProbeAt = now;
    if (typeof fetch !== 'function') return Promise.resolve({ status: 'unknown' });

    this.authProbe = fetch(this.getAuthProbeUrl(), {
      method: 'GET',
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${this.token}`,
        // Mesmo motivo do api.js: evita o interstitial do ngrok free.
        'ngrok-skip-browser-warning': 'true',
      },
    })
      .then(async (res) => {
        if (res.status !== 401) {
          return { status: res.status === 204 ? 'ok' : 'unknown' };
        }
        let reason = null;
        try {
          reason = res.headers?.get?.('X-Session-Invalid') || null;
        } catch {
          reason = null;
        }
        if (!reason) {
          try {
            const body = await res.json();
            if (body?.detail?.code === 'session_invalid') reason = body.detail.reason || null;
          } catch {
            // corpo não-JSON (proxy, nginx): fica 'invalid'
          }
        }
        return { status: 'invalid', reason: reason || 'invalid' };
      })
      .catch(() => ({ status: 'unknown' }))
      .finally(() => {
        this.authProbe = null;
      });
    return this.authProbe;
  }

  /**
   * Roda o probe e, se o backend recusou o token, encerra a sessão
   * (failAuthExpired) e devolve false; caso contrário devolve true e quem
   * chamou segue com o backoff/reconexão. O probe é assíncrono: quem chama
   * precisa reconferir `this.token` depois (logout no meio).
   */
  async verifyAuth() {
    const verdict = await this.probeAuth();
    if (verdict.status === 'invalid') {
      this.failAuthExpired(verdict.reason || 'invalid');
      return false;
    }
    return true;
  }

  /**
   * Connect to SSE server
   * @param {string} token - JWT authentication token
   */
  connect(token) {
    if (!token) {
      console.error('[SSE] No token provided');
      return false;
    }

    // Token já vencido: não abre o EventSource (o backend responderia 401 e
    // o laço de reconexão só geraria carga). Avisa e para.
    if (this.tokenExpired(token)) {
      this.failAuthExpired();
      return false;
    }
    // Token válido (ou flag desligada): próximo vencimento pode avisar de novo.
    this.authExpiredNotified = false;

    // Store token for reconnection
    this.token = token;

    // Prevent multiple connections
    if (this.eventSource && this.state === SSE_STATES.CONNECTED) {
      console.log('[SSE] Already connected');
      return true;
    }

    // Close existing connection if any
    this.disconnect(false);

    try {
      this.state = SSE_STATES.CONNECTING;
      this.emit('stateChange', { state: this.state });
      
      const sseUrl = this.getSSEUrl(token);
      console.log('[SSE] Connecting to:', sseUrl.replace(/token=.*/, 'token=***'));
      
      this.eventSource = new EventSource(sseUrl);
      
      // Connection opened
      this.eventSource.onopen = () => {
        console.log('[SSE] Connection opened');
        this.state = SSE_STATES.CONNECTED;
        this.reconnectAttempts = 0;
        this.emit('stateChange', { state: this.state });
      };
      
      // Generic message handler (for events without specific type)
      this.eventSource.onmessage = (event) => {
        this.handleMessage(event);
      };
      
      // Error handler
      this.eventSource.onerror = (error) => {
        this.handleError(error);
      };
      
      // Register specific event listeners
      this.registerEventListeners();
      
      return true;
    } catch (error) {
      console.error('[SSE] Connection error:', error);
      this.state = SSE_STATES.FAILED;
      this.emit('stateChange', { state: this.state, error });
      return false;
    }
  }

  /**
   * Register listeners for specific SSE event types
   */
  registerEventListeners() {
    if (!this.eventSource) return;
    
    // List of events to listen for
    const eventTypes = Object.values(SSE_EVENTS);
    
    eventTypes.forEach(eventType => {
      this.eventSource.addEventListener(eventType, (event) => {
        this.handleTypedMessage(eventType, event);
      });
    });
  }

  /**
   * Handle generic message
   */
  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      console.log('[SSE] Generic message:', data);
      this.emit('message', data);
    } catch (error) {
      console.error('[SSE] Error parsing message:', error);
    }
  }

  /**
   * Handle typed message (specific event type)
   */
  handleTypedMessage(eventType, event) {
    try {
      const message = JSON.parse(event.data);
      const { type, data, timestamp } = message;
      
      // Log message (except frequent ones)
      if (type !== SSE_EVENTS.HEARTBEAT && type !== SSE_EVENTS.PRESENCE_UPDATE) {
        console.log('[SSE] Received:', type, data);
      }
      
      // Update state on CONNECTED event
      if (type === SSE_EVENTS.CONNECTED) {
        this.state = SSE_STATES.CONNECTED;
        this.emit('stateChange', { state: this.state });
      }
      
      // Emit event to listeners
      this.emit(type, data, timestamp);
      
      // Also emit generic 'message' event
      this.emit('message', { type, data, timestamp });
      
    } catch (error) {
      console.error('[SSE] Error parsing typed message:', error);
    }
  }

  /**
   * Handle connection error
   */
  async handleError(error) {
    console.error('[SSE] Error:', error);

    // Token vencido (flag ligada): nem o reconnect manual (CLOSED) nem o
    // automático do browser (CONNECTING) vão passar — para aqui e avisa.
    if (this.tokenExpired()) {
      this.failAuthExpired();
      return;
    }

    // EventSource automatically reconnects on error
    // But we track state for UI feedback
    if (this.eventSource?.readyState === EventSource.CLOSED) {
      this.state = SSE_STATES.DISCONNECTED;
      this.emit('stateChange', { state: this.state });
      this.emit(SSE_EVENTS.ERROR, { error: 'Connection closed' });

      // Manual reconnect if browser doesn't auto-reconnect
      // (scheduleReconnect pergunta ao backend se o token ainda vale antes).
      // Conta a tentativa tambem aqui: sem isto o backoff ficava fixo em 1 s
      // quando o browser fecha a conexao (5xx, deploy, rede) e virava ~60
      // requisicoes por minuto por aba (medido em 13/10/2026).
      await this.scheduleReconnect();
      this.reconnectAttempts++;
    } else if (this.eventSource?.readyState === EventSource.CONNECTING) {
      this.state = SSE_STATES.RECONNECTING;
      this.reconnectAttempts++;
      this.emit('stateChange', {
        state: this.state,
        attempt: this.reconnectAttempts,
        maxAttempts: this.maxReconnectAttempts
      });
      // O browser vai tentar de novo sozinho; se o backend recusa o token,
      // fecha aqui em vez de deixar o laço rodar.
      await this.verifyAuth();
    }
  }

  /**
   * Schedule reconnection attempt
   */
  async scheduleReconnect() {
    if (this.tokenExpired()) {
      this.failAuthExpired();
      return;
    }
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[SSE] Max reconnection attempts reached');
      this.state = SSE_STATES.FAILED;
      this.emit('stateChange', { state: this.state });
      return;
    }

    // Antes de agendar: o backend ainda aceita este token? Se não, para aqui.
    if (!(await this.verifyAuth())) return;
    // Enquanto o probe rodava pode ter havido logout, ou outra chamada já
    // agendou a reconexão.
    if (!this.token || this.reconnectTimeout) return;

    // Exponential backoff: 1s, 2s, 4s, 8s, ... max 30s
    const delay = Math.min(
      1000 * Math.pow(2, this.reconnectAttempts),
      30000
    );

    console.log(`[SSE] Reconnecting in ${delay/1000}s (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

    this.reconnectTimeout = setTimeout(async () => {
      this.reconnectTimeout = null;
      // O token pode ter vencido durante o backoff (até 30 s).
      if (this.tokenExpired()) {
        this.failAuthExpired();
        return;
      }
      // ...ou ter sido recusado pelo backend (probe rate-limited: se rodou há
      // menos de 2 s é pulado e a reconexão segue).
      if (!(await this.verifyAuth())) return;
      if (this.token && this.state !== SSE_STATES.CONNECTED) {
        this.connect(this.token);
      }
    }, delay);
  }

  /**
   * Garante a conexão viva ao VOLTAR para o app (visibilitychange).
   *
   * No celular o SO congela a aba em background e mata o EventSource; o
   * backoff desiste após maxReconnectAttempts e o estado vira FAILED
   * TERMINAL — o realtime nunca voltava até re-login. Aqui zeramos o
   * contador e reconectamos na hora se não estiver aberto.
   */
  async ensureAlive() {
    if (!this.token) return;
    // Voltou para a aba com o token já vencido: não zera o contador nem
    // reconecta — era este caminho que mantinha o laço de 401 vivo.
    if (this.tokenExpired()) {
      this.failAuthExpired();
      return;
    }
    const open =
      this.eventSource && this.eventSource.readyState === EventSource.OPEN;
    if (open && this.state === SSE_STATES.CONNECTED) return;
    // Conexão morta: antes de zerar o contador, confirma que o backend ainda
    // aceita o token (um token recusado zeraria o laço de 401 a cada retorno).
    if (!(await this.verifyAuth())) return;
    if (!this.token) return;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.reconnectAttempts = 0;
    console.log('[SSE] ensureAlive: reconectando após retorno do background');
    this.connect(this.token);
  }

  /**
   * Disconnect from SSE server
   * @param {boolean} clearToken - Whether to clear stored token
   */
  disconnect(clearToken = true) {
    console.log('[SSE] Disconnecting...');
    
    // Clear reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    // Close EventSource
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    
    // Clear token if requested (prevents auto-reconnect)
    if (clearToken) {
      this.token = null;
      this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnect
    }
    
    this.state = SSE_STATES.DISCONNECTED;
    this.emit('stateChange', { state: this.state });
  }

  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   * @returns {function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Unsubscribe from an event
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  /**
   * Emit an event to all listeners
   * @param {string} event - Event name
   * @param {any} data - Event data
   * @param {string} timestamp - Event timestamp
   */
  emit(event, data, timestamp) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data, timestamp);
        } catch (error) {
          console.error(`[SSE] Error in listener for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.state === SSE_STATES.CONNECTED && 
           this.eventSource?.readyState === EventSource.OPEN;
  }

  /**
   * Get current state
   */
  getState() {
    return this.state;
  }

  /**
   * Reset reconnection attempts
   */
  resetReconnectAttempts() {
    this.reconnectAttempts = 0;
  }

  /**
   * Force reconnect
   */
  reconnect() {
    this.reconnectAttempts = 0;
    this.disconnect(false);
    if (this.token) {
      this.connect(this.token);
    }
  }
}

// Singleton instance
const sseService = new SSEService();

export default sseService;